import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { subscriptionServiceClient, pricingAdminClient } from '@/api/grpc/services';
import { SubscribeToPlanRequest, CancelSubscriptionRequest } from '../proto/gen/guardianai/v1/payment/payment_pb';
import { create } from '@bufbuild/protobuf';
import { EmptySchema } from '@bufbuild/protobuf/wkt';
import { useAuth } from './useAuth';
import { getErrorMessage, AUTH_ERRORS } from '@/utils/errorMessages';

// =============================================================================
// Types
// =============================================================================

export interface SubscriptionPlanDisplay {
  planId: string;
  name: string;
  description: string;
  price: {
    amount: number;
    currency: string;
    interval: 'monthly' | 'yearly';
  };
  features: string[];
  limits: {
    scansPerMonth: number;
    reportsPerMonth: number;
    storageGB: number;
  };
  popular?: boolean;
  productId: string;
  stripePriceId: string;
  stripeUrl: string;
  checkoutConfigured: boolean;
}

const getAuthUserId = (user: any): string => user?.userId || user?.id || '';

// =============================================================================
// Subscription Actions
// =============================================================================

export function useSubscribeToPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: SubscribeToPlanRequest) => {
      try {
        return await subscriptionServiceClient.subscribeToPlan(request);
      } catch (error) {
        const friendlyMessage = getErrorMessage(error, 'subscription');
        throw new Error(friendlyMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-status'] });
      queryClient.invalidateQueries({ queryKey: ['active-product-subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['payment-history'] });
    },
  });
}

export function useSubscriptionStatus(productId: string) {
  const { user } = useAuth();
  const userId = getAuthUserId(user);
  const normalizedProductId = productId.trim();

  return useQuery({
    queryKey: ['subscription-status', userId, normalizedProductId],
    queryFn: async () => {
      if (!userId) throw new Error(AUTH_ERRORS.NOT_AUTHENTICATED);
      if (!normalizedProductId) throw new Error('Product is required to check subscription status');

      try {
        const req: any = { userId, productId: normalizedProductId };
        return await subscriptionServiceClient.getSubscriptionStatus(req);
      } catch (error: unknown) {
        // Handle NOT_FOUND as a valid "no subscription" case
        // The backend returns NOT_FOUND when user has no active subscription
        const connectError = error as { code?: number | string; message?: string };
        const errorCode = connectError?.code;
        const errorMessage = connectError?.message?.toLowerCase() || '';

        // Check for NOT_FOUND error (code 5 in gRPC, or message contains "not found")
        if (errorCode === 5 || errorCode === 'not_found' || errorMessage.includes('not found')) {
          // Return a "no subscription" response instead of throwing
          return {
            subscriptionId: '',
            planId: 'none',
            status: 'none',
            currentPeriodStart: 0n,
            currentPeriodEnd: 0n,
            cancelAtPeriodEnd: false,
            validUntil: 0n,
            productId: normalizedProductId,
          };
        }

        // For other errors, throw as before
        const friendlyMessage = getErrorMessage(error, 'subscription');
        throw new Error(friendlyMessage);
      }
    },
    enabled: !!userId && !!normalizedProductId,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

export function useActiveProductSubscriptions() {
  const { user } = useAuth();
  const userId = getAuthUserId(user);

  return useQuery({
    queryKey: ['active-product-subscriptions', userId],
    queryFn: async () => {
      if (!userId) throw new Error(AUTH_ERRORS.NOT_AUTHENTICATED);
      const response = await subscriptionServiceClient.listActiveProductSubscriptions({
        userId,
      } as any);
      return response.subscriptions || [];
    },
    enabled: !!userId,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CancelSubscriptionRequest) => {
      try {
        return await subscriptionServiceClient.cancelSubscription(request);
      } catch (error) {
        const friendlyMessage = getErrorMessage(error, 'subscription');
        throw new Error(friendlyMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-status'] });
      queryClient.invalidateQueries({ queryKey: ['active-product-subscriptions'] });
    },
  });
}

// =============================================================================
// Subscription Plans - Fetched from PricingAdminService
// =============================================================================

/**
 * Fetch subscription plans from the pricing admin API.
 * 
 * Plans are managed via the admin dashboard (PricingAdministration).
 * This hook transforms the proto response into a UI-friendly format.
 */
export function useSubscriptionPlans(productId?: string) {
  return useQuery<SubscriptionPlanDisplay[], Error>({
    queryKey: ['subscription-plans', productId],
    queryFn: async () => {
      try {
        // Fetch plans from the centralized pricing admin service
        const response = await pricingAdminClient.listSubscriptionPlans({
          productSlug: productId || '',
        } as any);

        // Transform proto response to UI-friendly format
        const plans: SubscriptionPlanDisplay[] = response.plans
          .filter(plan => plan.isActive) // Only show active plans to users
          .map(plan => ({
            planId: plan.planId,
            name: plan.name,
            description: plan.description,
            price: {
              // Convert cents to dollars for display
              amount: Number(plan.priceCents) / 100,
              currency: plan.currency || 'USD',
              interval: (plan.interval === 'yearly' ? 'yearly' : 'monthly') as 'monthly' | 'yearly',
            },
            features: plan.features,
            limits: {
              scansPerMonth: plan.limits?.scansPerMonth ?? 10,
              reportsPerMonth: plan.limits?.reportsPerMonth ?? 5,
              storageGB: plan.limits?.storageGb ?? 1,
            },
            popular: plan.isPopular,
            productId: plan.productSlug,
            stripePriceId: plan.stripePriceId,
            stripeUrl: plan.stripeUrl,
            checkoutConfigured: Boolean(plan.stripePriceId || plan.stripeUrl),
          }));

        // Sort by price ascending (free/basic first)
        return plans.sort((a, b) => a.price.amount - b.price.amount);
      } catch (error) {
        const friendlyMessage = getErrorMessage(error, 'subscription');
        throw new Error(friendlyMessage);
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - prices don't change frequently
    gcTime: 30 * 60 * 1000, // Cache for 30 minutes
  });
}
